
import { useEffect, useCallback } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import dayjs from 'dayjs'

dayjs.extend(duration)
dayjs.extend(relativeTime)

import Timestamp from '../utils/Timestamp'
import LiveDuration from '../utils/LiveDuration'
import Alert from '../utils/Alert'
import Title from '../utils/Title'
import Spinner from '../utils/Spinner'

const GET_BUILD = gql`
	query GetBuildsInfo {
		latestFinishedBuild: latestBuild(status: "FINISHED") {
			buildId
			started
			finished
			status
		}
		latestBuild {
			buildId
			command
			started
			finished
			status
			log
		}
		latestDeploy {
			deployId
			buildId
			command
			started
			finished
			status
			log
		}
	}`

const BUILD_SUB = gql`
	subscription BuildSub {
		latestBuild {
			buildId
			command
			started
			finished
			status
			log
		}
	}`

const DEPLOY_SUB = gql`
	subscription DeploySub {
		latestDeploy {
			deployId
			buildId
			command
			started
			finished
			status
			log
		}
	}`

const START_BUILD = gql`
	mutation StartBuild {
		startBuild
	}`
const ABORT_BUILD = gql`
	mutation AbortBuild {
		abortBuild
	}`
const PUSH_TO_PRODUCTION = gql`
	mutation PushToProduction {
		pushToProduction
	}`
const GATSBY_CLEAN = gql`
	mutation GatsbyClean {
		gatsbyClean
	}`


export default function BuildPage() {

	const { data: buildData, loading: buildLoading, error: buildError, subscribeToMore } = useQuery(GET_BUILD)
	const [startBuild, { loading: startBuildLoading, error: startBuildError }] = useMutation(START_BUILD)
	const [abortBuild, { loading: abortBuildLoading, error: abortBuildError }] = useMutation(ABORT_BUILD)
	const [pushToProduction, { error: pushError }] = useMutation(PUSH_TO_PRODUCTION)
	const [gatsbyClean, { loading: gatsbyCleanLoading, error: gatsbyCleanError }] = useMutation(GATSBY_CLEAN)

	const build = buildData?.latestBuild
	const latestFinishedBuild = buildData?.latestFinishedBuild
	const deploy = buildData?.latestDeploy

	const isBuilding = build?.status === 'RUNNING'
	const isDeploying = deploy?.status === 'RUNNING'
	const canDeploy = !!latestFinishedBuild && !isBuilding && !isDeploying

	const handleStartBuild = useCallback(async () => {
		try {
			const response = await startBuild()
			if (!!response?.data?.startBuild)
				console.log("build started")
		} catch (error) { console.error(error)}
	}, [])

	async function handleAbort() {
		try {
			await abortBuild()
			console.log('aborted')
		} catch (e) { console.error(e) }
	}

	async function handlePushToProduction() {
		if (!window.confirm('Push the latest build to production? This replaces the live site.')) return
		try {
			await pushToProduction()
			console.log('pushed to production')
		} catch (e) { console.error(e) }
	}

	async function handleGatsbyClean() {
		if (!window.confirm('Clean the preview build cache (.cache and public)? Production is not affected.')) return
		try {
			await gatsbyClean()
			console.log('cleaned')
		} catch (e) { console.error(e) }
	}

	useEffect(() => {
		subscribeToMore({
			document: BUILD_SUB,
			variables: {},
			updateQuery: (prev, { subscriptionData: { data: { latestBuild } } }) => {
				return Object.assign({}, prev, { latestBuild })
			}
		})
		subscribeToMore({
			document: DEPLOY_SUB,
			variables: {},
			updateQuery: (prev, { subscriptionData: { data: { latestDeploy } } }) => {
				return Object.assign({}, prev, { latestDeploy })
			}
		})
	}, [])

	return <>

		<Title>Build</Title>

		<div className='Menu'>

			<button onClick={handleStartBuild} className='primary'
				disabled={isBuilding}
			>
				{ isBuilding ? 'Building...' : 'Start build' }
			</button>

			<button onClick={handleGatsbyClean} className='delete small'
				disabled={isBuilding || isDeploying || gatsbyCleanLoading}
				title='Clears the preview build cache (.cache and public). Never touches production.'
			>
				gatsby clean
			</button>

			{ (isBuilding || gatsbyCleanLoading) && <Spinner /> }

		</div>

		<Alert error={startBuildError} />
		<Alert error={gatsbyCleanError} />
		<Alert error={buildError} />

		{ (build?.status !== 'FINISHED' && !!latestFinishedBuild) && <>
			<h4>Latest successful build</h4>
			<FinishedBuild build={latestFinishedBuild} />
		</>}

		{ !!build && <h3>Last build</h3> }

		{ build?.status === 'FINISHED' &&
			<FinishedBuild build={build} /> }

		{ build?.status === 'RUNNING' && <p>
			Started <LiveDuration startIso={build.started} /> ago &mdash; <button onClick={() => handleAbort()} className='delete small'>
				{ abortBuildLoading ? "Aborting..." : "Abort" }
			</button>
		</p>}

		{ (build?.status === 'ERRORED' || build?.status === 'ABORTED' || build?.status === 'KILLED') && <p>
			❌ {build.status} <Timestamp live iso={build.finished} /> after <LiveDuration startIso={build.started} finishIso={build.finished} />.
		</p> }

		{ build?.status === 'ABORTING' || build?.status === 'KILLING' &&
			<p>⚠️ {build.status}</p> }

		{ !!build?.log &&
			<div className='BuildLog' dangerouslySetInnerHTML={{__html: build.log }} /> }

		<hr />

		<h3>Production</h3>

		<div className='Menu'>

			<button onClick={handlePushToProduction} className='primary'
				disabled={!canDeploy}
			>
				{ isDeploying ? 'Pushing to production...' : 'Push to production' }
			</button>

			{ isDeploying && <Spinner /> }

		</div>

		{ !latestFinishedBuild &&
			<p>Run a successful build before pushing to production.</p> }

		<Alert error={pushError} />

		{ !!deploy && <DeployState deploy={deploy} /> }

		{ !!deploy?.log &&
			<div className='BuildLog' dangerouslySetInnerHTML={{__html: deploy.log }} /> }

	</>
}


function DeployState({ deploy }) {
	if (deploy.status === 'RUNNING')
		return <p>Pushing to production &mdash; started <LiveDuration startIso={deploy.started} /> ago.</p>
	if (deploy.status === 'FINISHED')
		return <p>✅ Pushed to production <Timestamp live iso={deploy.finished} /> after <LiveDuration startIso={deploy.started} finishIso={deploy.finished} />.</p>
	if (deploy.status === 'ERRORED')
		return <p>❌ Push to production failed <Timestamp live iso={deploy.finished} />.</p>
	return null
}


function FinishedBuild({ build }) {
	return <>
		✅ Finished <Timestamp live iso={build.finished} /> after <LiveDuration startIso={build.started} finishIso={build.finished} /> &mdash; <a href={process.env.DAISY_PREVIEW_URL} target='DaisyPreview'>See preview</a>
	</>
}
